<script lang="ts" setup>
import { computed, type PropType } from 'vue' // Added computed import

interface SetupItem {
  timestamp: string | Date
  pumpId: number
  endPressure: number
  stableTime: number
  endAmount: number
}

interface SequenceItem {
  recipeId: number
  sequenceNo: number
  materialId: number
  materialName: string
  amount: number
  mixingTime: number
}

interface RecipeItem {
  timestamp: string | Date
  recipeId: number
  recipeName: string
  vcValue: number
  volume: number
  cement: number
  sequence: SequenceItem[]
}

const props = defineProps({
  setupData: {
    type: Array as PropType<SetupItem[][]>,
    required: true,
    default: () => []
  },
  recipeSequenceData: {
    type: Array as PropType<RecipeItem[]>,
    required: false,
    default: () => []
  }
});

interface ExtendedPumpSetup extends SetupItem {
  sessionId: number;
}

// Flattens [[P1,P2,P3,P4], [P1,P2,P3,P4]] into a single list with IDs
const flattenedSetupData = computed<ExtendedPumpSetup[]>(() => {
  if (!props.setupData) return [];
  return props.setupData.flatMap((session, sessionIndex) => 
    session.map(item => ({
      ...item,
      sessionId: sessionIndex 
    }))
  );
});

// Colors every other 4-pump batch for better readability
const getRowProps = (data: any) => {
  const item = data.item as ExtendedPumpSetup;
  return {
    class: item.sessionId % 2 === 0 ? 'bg-grey-lighten-5' : 'bg-white'
  };
};

const formatDate = (date: string | Date | undefined) => {
  if (!date) return "N/A";
  const d = new Date(date);
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const fmt = (val: number | undefined | null) => {
  if (val === undefined || val === null) return '0';
  return val.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
};

const getVisibleSteps = (sequence: SequenceItem[]) => {
  if (!sequence) return [];
  return sequence.filter(step => 
    step.materialName !== 'None' || 
    step.amount !== 0 || 
    step.mixingTime !== 0
  );
};

const setupHeaders = [
  { title: 'Time Stamp', key: 'timestamp', align: 'start', sortable: false },
  { title: 'Pump ID', key: 'pumpId', align: 'start', sortable: false },
  { title: 'End Pressure (bar)', key: 'endPressure', align: 'end' },
  { title: 'Stable Time (s)', key: 'stableTime', align: 'end' },
  { title: 'End Amount (L)', key: 'endAmount', align: 'end' },
] as const;

const sequenceHeaders = [
  { title: 'Step', key: 'sequenceNo', align: 'start' },
  { title: 'Material', key: 'materialName', align: 'start' },
  { title: 'Amount (L)', key: 'amount', align: 'end' },
  { title: 'Mixing Time (s)', key: 'mixingTime', align: 'end' },
] as const;

const getSequenceRowProps = (data: { item: SequenceItem }) => {
  if (data.item.amount === 0) return { class: 'bg-grey-lighten-5 text-medium-emphasis' };
  return {};
};
</script>

<template>
  <v-container fluid class="pa-4 pa-md-6">
    <!-- Pump Setup History -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4 py-3">
        <v-icon icon="mdi-history" size="small" class="mr-2"></v-icon>
        Pump Setup History (4-Pump Batches)
      </v-card-title>
      <v-divider></v-divider>
      
      <v-alert
        v-if="!flattenedSetupData.length"
        type="info"
        variant="tonal"
        text="No Setup data available."
        class="ma-4"
      ></v-alert>

      <v-data-table
        v-else
        :headers="setupHeaders"
        :items="flattenedSetupData"
        :row-props="getRowProps"
        density="compact"
        hover
      >
        <!-- Logic to only show timestamp once per batch of 4 -->
        <template v-slot:item.timestamp="{ item, index }">
          <span v-if="index === 0 || item.sessionId !== flattenedSetupData[index - 1].sessionId" 
                class="font-weight-bold text-primary text-body-2">
            {{ formatDate(item.timestamp) }}
          </span>
          <span v-else class="text-grey-lighten-2 text-caption">"</span>
        </template>
        
        <template v-slot:item.pumpId="{ item }">
          <v-chip size="x-small" :color="item.sessionId % 2 === 0 ? 'blue-grey-darken-1' : 'primary'" variant="flat" label class="font-weight-bold px-2">
            PUMP {{ item.pumpId }}
          </v-chip>
        </template>

        <template v-slot:item.endPressure="{ item }">
          <span class="font-monospace text-body-2">{{ fmt(item.endPressure) }}</span>
        </template>
        
        <template v-slot:item.stableTime="{ item }">
          <span class="font-monospace text-body-2">{{ item.stableTime }}s</span>
        </template>
        
        <template v-slot:item.endAmount="{ item }">
          <span class="font-monospace text-body-2">{{ fmt(item.endAmount) }}</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Recipe Section -->
    <div>
      <h3 class="text-h5 font-weight-bold text-primary mb-4 d-flex align-center">
        <v-icon icon="mdi-flask-outline" color="primary" class="mr-2"></v-icon>
        Recipe & Sequence Information
      </h3>

      <v-alert
        v-if="!recipeSequenceData || recipeSequenceData.length === 0"
        type="info"
        variant="tonal"
        text="No Recipe data available."
      ></v-alert>

      <template v-else>
        <v-card
          v-for="(recipe, index) in recipeSequenceData"
          :key="'recipe-' + index"
          class="mb-6" elevation="2"
          style="border-left: 4px solid #8b5cf6 !important;"
        >
          <v-card-item class="bg-grey-lighten-4 py-3">
            <template v-slot:prepend>
              <v-chip color="grey-darken-3" label size="small" class="font-weight-bold mr-2">
                ID: {{ recipe.recipeId }}
              </v-chip>
              <span class="text-h6 font-weight-bold text-grey-darken-3">{{ recipe.recipeName }}</span>
            </template>
            <template v-slot:append>
              <div class="d-flex align-center gap-2 flex-wrap justify-end">
                <v-chip size="small" variant="outlined" class="bg-white">VC: {{ fmt(recipe.vcValue) }}</v-chip>
                <v-chip size="small" variant="outlined" class="bg-white">Vol: {{ fmt(recipe.volume) }}L</v-chip>
                <v-chip size="small" variant="outlined" class="bg-white">Cem: {{ fmt(recipe.cement) }}kg</v-chip>
                <span class="text-caption text-medium-emphasis ml-2 d-none d-sm-inline-block">{{ formatDate(recipe.timestamp) }}</span>
              </div>
            </template>
          </v-card-item>
          <v-divider></v-divider>

          <v-card-text class="pa-4">
            <v-data-table
              :headers="sequenceHeaders"
              :items="getVisibleSteps(recipe.sequence)"
              :row-props="getSequenceRowProps"
              density="compact"
              class="border rounded-lg"
              hide-default-footer
            >
              <template v-slot:item.sequenceNo="{ item }">
                <span class="text-medium-emphasis">#{{ item.sequenceNo }}</span>
              </template>
              <template v-slot:item.amount="{ item }"><span class="font-monospace text-body-2">{{ fmt(item.amount) }}</span></template>
              <template v-slot:item.mixingTime="{ item }"><span class="font-monospace text-body-2">{{ item.mixingTime }}s</span></template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </template>
    </div>
  </v-container>
</template>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
.font-monospace {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace !important;
}
</style>